import { FOREIGNER } from "@/constants/constants"

/**
 * International SEO helpers: `<html lang>`, `rel="canonical"` and
 * `rel="alternate" hreflang` tags.
 *
 * IMPORTANT — what the `/en` prefix actually means here:
 *
 *   `/<path>`     default variant. Indonesia-targeted (IDR, local payment
 *                 methods). Content renders in `id` OR `en` depending on the
 *                 visitor (user preferred_locale, PREFERRED_LOCALE in
 *                 localStorage, browser language, `?lang=`).
 *   `/en/<path>`  foreigner variant. Locale is forced to FOREIGNER.LOCALE, so
 *                 the content is always English, and pricing/checkout switch to
 *                 USD + Stripe (see `isForeigner` in pages/account/Pay.vue).
 *
 * So `/en` is an audience/currency variant that happens to be English-only —
 * NOT a plain language folder, and the default variant is not Indonesian-only.
 * Because of that:
 *   - `<html lang>` follows the locale actually rendered, never the URL.
 *   - hreflang/canonical follow the URL, since the URL is what Google indexes.
 *     The default URL is annotated `id` (the Indonesia-targeted variant) and
 *     `x-default` (the entry point that adapts to the visitor); the `/en` URL is
 *     annotated `en`.
 *
 * URLs are emitted WITHOUT a trailing slash (except the site root, where the
 * slash is the path). nginx 301-redirects `^/(.*)/$` to the slashless form
 * (nginx/production/conf.d/https.conf), and hreflang must point at final URLs
 * rather than redirects.
 */

export const SITE_ORIGIN = "https://goro.id"

const HREFLANG = {
  ID: "id",
  EN: "en",
  DEFAULT: "x-default"
}

const HTML_LANG = {
  ID: "id",
  EN: "en"
}

// Marks the tags this module owns so navigations never duplicate or orphan
// them. The static tags in public/index.html carry the same attribute, which
// lets the router take them over on the first navigation.
const MANAGED_ATTR = "data-seo-managed"

/**
 * Drop a trailing slash to match the nginx redirect, so one page never yields
 * two canonical URLs.
 * @param {string} path
 * @returns {string}
 */
const normalizePath = (path) => {
  if (!path || path === "/") {
    return "/"
  }
  return path.replace(/\/+$/, "") || "/"
}

/**
 * The default-variant path of a page (i.e. without the foreigner prefix).
 * @param {string} path route path, with or without the `/en` prefix
 * @returns {string}
 */
export const toDefaultPath = (path) => {
  const normalized = normalizePath(path)
  const prefix = FOREIGNER.ROUTE_PATH_PREFIX
  if (normalized === prefix) {
    return "/"
  }
  if (normalized.startsWith(`${prefix}/`)) {
    return normalized.slice(prefix.length) || "/"
  }
  return normalized
}

/**
 * The foreigner-variant path for a default path. `/` maps to `/en` (no trailing
 * slash — see the module note on the nginx redirect).
 * @param {string} defaultPath
 * @returns {string}
 */
export const toForeignerPath = (defaultPath) => {
  const prefix = FOREIGNER.ROUTE_PATH_PREFIX
  return defaultPath === "/" ? prefix : `${prefix}${defaultPath}`
}

/**
 * @param {string} path route path
 * @returns {boolean} whether the path is a foreigner (`/en`) URL
 */
export const isForeignerPath = (path) => toDefaultPath(path) !== normalizePath(path)

/**
 * Map an i18n locale (`id`, `en`, `en-UK`) to a valid BCP 47 `lang` value.
 * `en-UK` is not a valid tag (the region subtag for the UK is `GB`), so the
 * language subtag alone is used.
 * @param {string} locale
 * @returns {string}
 */
const toHtmlLang = (locale) => {
  const value = (locale || "").toLowerCase()
  return value === HTML_LANG.ID || value.startsWith(`${HTML_LANG.ID}-`)
    ? HTML_LANG.ID
    : HTML_LANG.EN
}

const buildUrl = (path) => `${SITE_ORIGIN}${path}`

/**
 * The canonical absolute URL for a route path — the same value applyAlternateLinks
 * writes into rel=canonical. Use it anywhere a page has to state its own URL
 * (structured data, share tags) so the signals cannot drift apart.
 * @param {string} path route path, with or without the `/en` prefix
 * @returns {string}
 */
export const toCanonicalUrl = (path) =>
  isForeignerPath(path)
    ? buildUrl(toForeignerPath(toDefaultPath(path)))
    : buildUrl(toDefaultPath(path))

const removeManagedLinks = () => {
  document.querySelectorAll(`link[${MANAGED_ATTR}]`).forEach((link) => link.remove())
}

/**
 * Point a share-URL meta tag at the current page. Unlike canonical these are set
 * for every route, indexable or not: a noindex page can still be shared, and a
 * stale URL here makes every share preview resolve to whatever the shell
 * hardcoded. Values are per-route, so they cannot live in public/index.html.
 * @param {{ selector: string, url: string }} params
 */
const applyShareUrlMeta = ({ selector, url }) => {
  const meta = document.head.querySelector(selector)
  if (meta) {
    meta.setAttribute("content", url)
  }
}

/**
 * @param {{ rel: string, href: string, hreflang?: string }} params
 */
const appendManagedLink = ({ rel, href, hreflang }) => {
  const link = document.createElement("link")
  link.setAttribute("rel", rel)
  link.setAttribute("href", href)
  if (hreflang) {
    link.setAttribute("hreflang", hreflang)
  }
  link.setAttribute(MANAGED_ATTR, "true")
  document.head.appendChild(link)
}

/**
 * Declare the language of the rendered document for crawlers and assistive
 * technologies. Reflects the locale on screen, which on default-variant URLs is
 * not always Indonesian. Safe to call on every locale change.
 * @param {string} locale i18n locale (`id`, `en`, `en-UK`)
 */
export const applyHtmlLang = (locale) => {
  try {
    const lang = toHtmlLang(locale)
    if (document.documentElement.getAttribute("lang") !== lang) {
      document.documentElement.setAttribute("lang", lang)
    }
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.error(
        `[seo.applyHtmlLang] Failed to set html lang for locale "${locale}":`,
        error,
        error && error.stack
      )
    }
  }
}

/**
 * Rewrite the canonical + hreflang set for the current page.
 *
 * Nothing is emitted for a page that is not indexable — a soft 404, or anything
 * carrying a `noindex` directive. Both would be actively harmful:
 *   - a self-referencing canonical on a soft 404 asks Google to index the dead
 *     URL as a real page;
 *   - a canonical that drops the query string turns a `noindex` query URL into a
 *     cross-URL canonical, which can transfer the `noindex` to the clean page;
 *   - hreflang pointing at `noindex`/disallowed URLs is discarded and reported
 *     as an error, burying real problems on the pages that matter.
 *
 * The hreflang set additionally requires a foreigner variant to exist — pointing
 * it at a 404 would invalidate the whole set.
 *
 * @param {{ path: string, isIndexable: boolean, hasForeignerVariant: boolean }} params
 */
export const applyAlternateLinks = ({ path, isIndexable, hasForeignerVariant }) => {
  try {
    removeManagedLinks()

    const defaultPath = toDefaultPath(path)
    const defaultUrl = buildUrl(defaultPath)
    const foreignerUrl = buildUrl(toForeignerPath(defaultPath))
    const selfUrl = isForeignerPath(path) ? foreignerUrl : defaultUrl

    // Share URLs are set for every route, before the indexability gate below.
    applyShareUrlMeta({ selector: 'meta[property="og:url"]', url: selfUrl })
    applyShareUrlMeta({ selector: 'meta[name="twitter:url"]', url: selfUrl })

    if (!isIndexable) {
      return
    }

    appendManagedLink({
      rel: "canonical",
      href: selfUrl
    })

    if (!hasForeignerVariant) {
      return
    }

    appendManagedLink({ rel: "alternate", href: defaultUrl, hreflang: HREFLANG.ID })
    appendManagedLink({ rel: "alternate", href: foreignerUrl, hreflang: HREFLANG.EN })
    appendManagedLink({ rel: "alternate", href: defaultUrl, hreflang: HREFLANG.DEFAULT })
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.error(
        `[seo.applyAlternateLinks] Failed to update canonical/hreflang tags for path "${path}":`,
        error,
        error && error.stack
      )
    }
  }
}

/**
 * Update every language signal for a resolved route.
 * @param {{ path: string, locale: string, isIndexable: boolean, hasForeignerVariant: boolean }} params
 */
export const applyLanguageTags = ({ path, locale, isIndexable, hasForeignerVariant }) => {
  applyHtmlLang(locale)
  applyAlternateLinks({ path, isIndexable, hasForeignerVariant })
}
